import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  costCenterCreate,
  costCenterDetail,
  costCenterUpdate,
  type CostCenterDetail,
  type CreateCostCenterRequest,
} from '@/api/domains/organization/costCenters';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useSidePanelLoading, useUI } from '@/hooks/ui';
import {
  createCostCenterSchema,
  type CostCenterFormData,
  updateCostCenterSchema,
} from '@/schemas/organization';
import { SECTIONS } from '@/types/sections';
import { formatToDdMmYyyy, injectValidationErrors } from '@/utils';


type CostCenterFormMode = 'create' | 'edit';

export interface CostCenterFormRecord {
  mode?: CostCenterFormMode;
  costCenterId?: number;
}

interface UseCostCenterFormPanelParams {
  onSubmit?: () => void;
  readOnly?: boolean;
  record?: CostCenterFormRecord;
}

interface CostCenterFormMetadata {
  id: string;
  createdDate: string;
  createdBy: string;
  modifiedDate: string;
  modifiedBy: string;
}

export interface UseCostCenterFormPanelResult {
  codeValue: string;
  nameValue: string;
  descriptionValue: string;
  metadata: CostCenterFormMetadata | null;
  isEditMode: boolean;
  effectiveReadOnly: boolean;
  isSubmitting: boolean;
  isReady: boolean;
  canSave: boolean;
  handleCodeChange: (value: string) => void;
  handleNameChange: (value: string) => void;
  handleDescriptionChange: (value: string) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
  errors: ReturnType<typeof useForm<CostCenterFormData>>['formState']['errors'];
  isSubmitted: boolean;
}

const mapMetadataFromDetail = (detail: CostCenterDetail): CostCenterFormMetadata => ({
  id: String(detail.id),
  createdDate: formatToDdMmYyyy(detail.createdDate) || '-',
  createdBy: detail.createdBy || '-',
  modifiedDate: formatToDdMmYyyy(detail.modifiedDate) || '-',
  modifiedBy: detail.modifiedBy || '-',
});

export function useCostCenterFormPanel(
  { onSubmit, readOnly, record }: UseCostCenterFormPanelParams,
): UseCostCenterFormPanelResult {
  const { t: translate } = useTranslation();
  const { canSection } = usePagePermissions();
  const { closeSidePanel, showNotification, showApiError } = useUI();
  const { setLoading } = useSidePanelLoading();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [metadata, setMetadata] = useState<CostCenterFormMetadata | null>(null);

  const latestRequestIdRef = useRef(0);
  const costCenterId = record?.costCenterId;
  const mode = record?.mode ?? (costCenterId ? 'edit' : 'create');
  const isEditMode = mode === 'edit';
  const effectiveReadOnly = !!readOnly;
  const canSave = isEditMode
    ? canSection(SECTIONS.CENTROS_COSTO, 'edit')
    : canSection(SECTIONS.CENTROS_COSTO, 'create');

  const {
    handleSubmit: rhfHandleSubmit,
    setValue,
    watch,
    setError,
    formState,
  } = useForm<CostCenterFormData>({
    resolver: zodResolver(isEditMode ? updateCostCenterSchema : createCostCenterSchema),
    defaultValues: { code: '', name: '', description: '' },
    mode: 'onSubmit',
  });

  const { errors, isSubmitted } = formState;
  const codeValue = watch('code') || '';
  const nameValue = watch('name') || '';
  const descriptionValue = watch('description') || '';

  // Bootstrap
  useEffect(() => {
    let isMounted = true;
    const bootstrap = async () => {
      if (isEditMode && !costCenterId) {
        setIsBootstrapping(false);
        showNotification('error', translate('organization:messages.costCenterLoadError'));
        closeSidePanel();
        return;
      }

      setIsBootstrapping(true);
      setLoading(true);
      const requestId = ++latestRequestIdRef.current;

      if (!isEditMode) {
        setIsBootstrapping(false);
        setLoading(false);
        return;
      }

      const currentCostCenterId = costCenterId as number;
      const detailResponse = await costCenterDetail.getById(currentCostCenterId);

      if (!isMounted || requestId !== latestRequestIdRef.current) return;

      if (!detailResponse.success || !detailResponse.data) {
        showApiError(detailResponse, () => translate('organization:messages.costCenterLoadError'));
        closeSidePanel();
        setIsBootstrapping(false);
        setLoading(false);
        return;
      }

      setValue('code', detailResponse.data.code, { shouldValidate: false });
      setValue('name', detailResponse.data.name, { shouldValidate: false });
      setValue('description', detailResponse.data.description, { shouldValidate: false });
      setMetadata(mapMetadataFromDetail(detailResponse.data));
      setIsBootstrapping(false);
      setLoading(false);
    };

    void bootstrap();
    return () => {
      isMounted = false;
      setLoading(false);
    };
  }, [
    closeSidePanel, costCenterId, isEditMode, setLoading, setValue,
    showApiError, showNotification, translate,
  ]);

  const isReady = useMemo(() => {
    const hasCode = codeValue.trim().length > 0;
    const hasName = nameValue.trim().length > 0;
    const hasDescription = descriptionValue.trim().length > 0;
    return hasCode && hasName && hasDescription && !isBootstrapping;
  }, [codeValue, descriptionValue, isBootstrapping, nameValue]);

  const handleCodeChange = useCallback(
    (value: string) => { setValue('code', value, { shouldValidate: true, shouldDirty: true }); },
    [setValue],
  );

  const handleNameChange = useCallback(
    (value: string) => { setValue('name', value, { shouldValidate: true, shouldDirty: true }); },
    [setValue],
  );

  const handleDescriptionChange = useCallback(
    (value: string) => { setValue('description', value, { shouldValidate: true, shouldDirty: true }); },
    [setValue],
  );

  const onValid = useCallback(async (data: CostCenterFormData) => {
    if (effectiveReadOnly || !canSave) return;
    setIsSubmitting(true);

    const requestPayload: CreateCostCenterRequest = {
      code: data.code.trim(),
      name: data.name.trim(),
      description: data.description.trim(),
    };

    const response = isEditMode && costCenterId
      ? await costCenterUpdate.update(costCenterId, requestPayload)
      : await costCenterCreate.create(requestPayload);

    if (response.success && response.data) {
      showNotification(
        'success',
        translate(isEditMode ? 'organization:messages.costCenterUpdated' : 'organization:messages.costCenterCreated'),
      );
      window.dispatchEvent(new CustomEvent('cost-center-form-success'));
      onSubmit?.();
      closeSidePanel();
      setIsSubmitting(false);
      return;
    }

    if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
      injectValidationErrors(response.error.validationErrors, { setError });
      setIsSubmitting(false);
      return;
    }

    showApiError(response, translate(
      isEditMode
        ? 'organization:messages.costCenterUpdateError'
        : 'organization:messages.costCenterCreateError',
    ));
    setIsSubmitting(false);
  }, [
    canSave,
    closeSidePanel,
    costCenterId,
    effectiveReadOnly,
    isEditMode,
    onSubmit,
    setError,
    showApiError,
    showNotification,
    translate,
  ]);

  const onInvalid = useCallback(() => {}, []);

  const handleSubmit = useCallback((event?: FormEvent<HTMLFormElement>) => {
    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    void rhfHandleSubmit(onValid, onInvalid)(event);
  }, [onInvalid, onValid, rhfHandleSubmit]);

  return {
    codeValue,
    nameValue,
    descriptionValue,
    metadata,
    isEditMode,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    canSave,
    handleCodeChange,
    handleNameChange,
    handleDescriptionChange,
    handleSubmit,
    errors,
    isSubmitted,
  };
}

export default useCostCenterFormPanel;
